import React from "react";
import { Link } from "react-router";
import styled from "@emotion/styled";
import {
  fontFamily,
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  border as borderToken,
  interactive,
  surface,
  text,
} from "@atlas-int/tokens";
import { packageRegistry } from "./packageRegistry";
import DependencyGraph from "./components/DependencyGraph";

const Title = styled.h1`
  font-size: ${fontSize["3xl"]};
  font-weight: ${fontWeight.bold};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const Subtitle = styled.p`
  font-size: ${fontSize.lg};
  color: ${text.secondary};
  margin: 0 0 ${spacing[12]} 0;
`;

const SectionTitle = styled.h2`
  font-size: ${fontSize["2xl"]};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: ${spacing[12]} 0 ${spacing[6]} 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${spacing[6]};
`;

const PackageCard = styled(Link)`
  display: block;
  padding: ${spacing[8]};
  background-color: ${surface.raised};
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.md};
  text-decoration: none;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${interactive.default};
  }
`;

const CardName = styled.div`
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.bold};
  color: ${text.primary};
  margin-bottom: ${spacing[2]};
`;

const CardDescription = styled.div`
  font-size: ${fontSize.sm};
  color: ${text.secondary};
  line-height: 1.5;
  margin-bottom: ${spacing[4]};
`;

const CardMeta = styled.div`
  display: flex;
  gap: ${spacing[2]};
  flex-wrap: wrap;
`;

const MetaBadge = styled.span`
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.xs};
  padding: ${spacing[1]} ${spacing[2]};
  background-color: ${surface.muted};
  border-radius: ${borderRadius.base};
  color: ${text.muted};
`;

const ExportCount = styled.span`
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.xs};
  padding: ${spacing[1]} ${spacing[2]};
  background-color: ${interactive.default};
  border-radius: ${borderRadius.base};
  color: ${text.onDark};
`;

const PackagesLanding: React.FC = () => {
  return (
    <div>
      <Title>Atlas Package System</Title>
      <Subtitle>
        5 packages providing design tokens, UI components, data hooks,
        visualization controls, and chart implementations.
      </Subtitle>

      <DependencyGraph />

      <SectionTitle>Packages</SectionTitle>
      <Grid>
        {packageRegistry.map((pkg) => {
          const totalExports = pkg.exportCategories.reduce(
            (sum, cat) => sum + cat.exports.length,
            0,
          );
          return (
            <PackageCard key={pkg.id} to={`/packages/${pkg.id}`}>
              <CardName>{pkg.name}</CardName>
              <CardDescription>{pkg.description}</CardDescription>
              <CardMeta>
                <ExportCount>{totalExports} exports</ExportCount>
                {pkg.dependencies.length > 0 && (
                  <MetaBadge>
                    {pkg.dependencies.length} internal dep
                    {pkg.dependencies.length > 1 ? "s" : ""}
                  </MetaBadge>
                )}
                {pkg.subExports && (
                  <MetaBadge>
                    {pkg.subExports.length} sub-export
                    {pkg.subExports.length > 1 ? "s" : ""}
                  </MetaBadge>
                )}
              </CardMeta>
            </PackageCard>
          );
        })}
      </Grid>
    </div>
  );
};

export default PackagesLanding;
